import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, Observable, of, pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { RegistrationCandidateService } from './infra/registration-candidate.service';
import { REGISTRATION_CANDIDATE_LIST_PAGE_SIZE } from './entities/registration-candidate.model';
import type {
  IRegistrationCandidateList,
  IRegistrationCandidateListItem,
  IRegistrationCandidateListPage,
  TRegistrationCandidateStatus,
} from './entities/registration-candidate.model';

interface IRegistrationCandidateListState {
  /** The link whose list is shown. `null` until one is. */
  linkId: string | null;
  status: TRegistrationCandidateStatus;
  /** 0-based. */
  page: number;
  /** The rows of that page of that list of that link, or none: never the rows of another. */
  items: IRegistrationCandidateListItem[];
  /** How many that list of that link has, as the Worker last answered. */
  total: number;
  /** What the last delete did, as the Worker answered. The notice is worded from it. */
  removed: 'trashed' | 'deleted' | null;
}

const initialState: IRegistrationCandidateListState = {
  linkId: null,
  status: 'pending',
  page: 0,
  items: [],
  total: 0,
  removed: null,
};

/**
 * The registrations of one link, a page of one of its three lists at a time
 * (waiting, handled, in the trash), and the two writes made from a row: delete
 * and restore.
 *
 * Next to `RegistrationCandidateStore`, which holds the one registration the
 * counter is conferring, and to `RegistrationLinksStore`, which holds the
 * counts of each list: this store keeps no copy of them.
 *
 * Neither write changes the rows or the total, and neither asks the list
 * again: whoever started the write reloads, here and in the links store.
 */
export const RegistrationCandidateListStore = signalStore(
  { providedIn: 'root' },
  withState<IRegistrationCandidateListState>(initialState),
  withMethods(
    (store, service = inject(RegistrationCandidateService), errorStore = inject(ErrorStore)) => {
      /**
       * Fires on `reset`: whatever is out stops being listened to. The three
       * methods below are subscribed once and for good, so an answer of the
       * last visit would otherwise be written into the next.
       */
      const cancelled$ = new Subject<void>();

      /**
       * Asks for a page and gives the answer to show.
       *
       * A page past the end is not one: it comes empty while the list has rows,
       * because the last row of the page was deleted here, or rows left through
       * another station or the nightly purge. The last page the list still has
       * is asked instead, and again while the answer is past the end, so that
       * "loaded with a total of zero" stays the only empty list.
       *
       * It ends: each page asked is lower than the one before, and page 0 is
       * taken as answered whatever it holds. The Worker reads rows and total in
       * one batch, so an empty page already means the last one is lower; the
       * `page - 1` holds that even for an answer that says otherwise.
       */
      const askPage = ({
        linkId,
        status,
        page,
      }: IRegistrationCandidateListPage): Observable<IRegistrationCandidateList> =>
        service
          .list({
            linkId,
            status,
            limit: REGISTRATION_CANDIDATE_LIST_PAGE_SIZE,
            offset: page * REGISTRATION_CANDIDATE_LIST_PAGE_SIZE,
          })
          .pipe(
            switchMap((answer) => {
              const pastTheEnd = answer.items.length === 0 && answer.total > 0 && page > 0;
              if (!pastTheEnd) return of(answer);

              const lastPage = Math.min(
                page - 1,
                Math.ceil(answer.total / REGISTRATION_CANDIDATE_LIST_PAGE_SIZE) - 1
              );
              // A reload kept its rows on screen, and they are of the page that
              // just came empty: they go with it.
              patchState(store, { page: lastPage, items: [] });
              return askPage({ linkId, status, page: lastPage });
            })
          );

      return {
        /**
         * Shows a page of a list of a link. The rows on screen are always of
         * the three in state, or none: another link or another status clears
         * rows and total at once, another page of the same list clears the
         * rows, and only the same three asked again (a reload) keep both while
         * the answer is out.
         *
         * A failure clears rows and total, whatever the status: no row is left
         * to act on once nobody knows whether it is still there.
         */
        loadList: rxMethod<IRegistrationCandidateListPage>(
          pipe(
            tap(({ linkId, status, page }) => {
              const sameList = store.linkId() === linkId && store.status() === status;
              const samePage = sameList && store.page() === page;

              patchState(store, {
                linkId,
                status,
                page,
                items: samePage ? store.items() : [],
                total: sameList ? store.total() : 0,
                ...setPropLoading('loadList'),
              });
            }),
            switchMap((asked) =>
              askPage(asked).pipe(
                // On the whole of it, so that a step back that is out goes too.
                takeUntil(cancelled$),
                tapResponse({
                  next: ({ items, total }) =>
                    patchState(store, { items, total, ...setPropLoaded('loadList') }),
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, { items: [], total: 0, ...setPropError('loadList', error) });
                  },
                })
              )
            )
          )
        ),

        /**
         * Deletes a registration. The Worker says where it ended up: `trashed`
         * for one that was waiting, `deleted` for one already handled. A row
         * confirmed as waiting can still come back `deleted`, when the counter
         * handled it meanwhile.
         *
         * `exhaustMap`: `removed` is the answer of the one delete that was sent.
         * A second one while it is in flight is dropped.
         */
        remove: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { removed: null, ...setPropLoading('remove') })),
            exhaustMap((candidateId) =>
              service.remove(candidateId).pipe(
                takeUntil(cancelled$),
                tapResponse({
                  next: ({ status }) =>
                    patchState(store, { removed: status, ...setPropLoaded('remove') }),
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, { ...setPropError('remove', error) });
                  },
                })
              )
            )
          )
        ),

        /**
         * Takes a registration out of the trash.
         *
         * The Worker refuses with two 409s that only the sentence tells apart:
         * the registration was handled meanwhile, or the link got another one
         * of that CPF. Neither is read here: the error goes to the alerts and
         * to `restoreError` as it came.
         */
        restore: rxMethod<string>(
          pipe(
            tap(() => patchState(store, { ...setPropLoading('restore') })),
            exhaustMap((candidateId) =>
              service.restore(candidateId).pipe(
                takeUntil(cancelled$),
                tapResponse({
                  next: () => patchState(store, { ...setPropLoaded('restore') }),
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, { ...setPropError('restore', error) });
                  },
                })
              )
            )
          )
        ),

        /**
         * Clears the outcome of the two writes. The store is root-scoped, so a
         * `removeLoaded` left over from the delete before would close the next
         * confirmation as it opens.
         */
        blankWriteState: (): void =>
          patchState(store, { removed: null, ...setPropInit('remove', 'restore') }),

        /**
         * Back to the start, and whatever is out is cancelled. The store is
         * root-scoped: the rows and the flags of the last visit would otherwise
         * show in the next, and an answer still out would land in it. A write
         * left running would also hold its `exhaustMap`: the first delete of
         * the next visit would be dropped, and the end of the old one taken for
         * the end of it.
         *
         * `resetLookup` of `RegistrationCandidateStore` meets that by keeping
         * its write loading instead. Here the answer is given up: the Worker
         * may still make the write, but each is a single atomic one, repeating
         * it leaves the row where the first put it, and the next visit asks the
         * list and the links again, so nothing in state is left saying it was
         * not made.
         */
        reset: (): void => {
          cancelled$.next();
          patchState(store, {
            ...initialState,
            ...setPropInit('loadList', 'remove', 'restore'),
          });
        },
      };
    }
  ),
  withReqState('loadList', 'remove', 'restore'),
  // Below the methods above, which it calls.
  withMethods((store) => ({
    /**
     * Asks again for the page on screen, keeping its rows meanwhile. Nothing to
     * ask before a link is shown.
     */
    reload: (): void => {
      const linkId = store.linkId();
      if (linkId === null) return;

      store.loadList({ linkId, status: store.status(), page: store.page() });
    },
  })),
  // Below the request states, which it reads.
  withComputed((store) => ({
    /** Never below 1: a list with no rows still has its first page. */
    pageCount: computed<number>(() =>
      Math.max(1, Math.ceil(store.total() / REGISTRATION_CANDIDATE_LIST_PAGE_SIZE))
    ),
    /** Answered, and with nothing in it. Not true while loading, nor after a failure. */
    isEmpty: computed<boolean>(() => !!store.loadListLoaded() && store.total() === 0),
  }))
);
